'use strict'
const axios = require('axios');

// Production Feature Flags endpoint
const FF_ENDPOINT = 'https://api.cloud.nuskin.com/feature-flags/v1/proxy';
const OVERRIDE_FLAG_ENABLE_PARAM = '$enableFeatureFlags';
const OVERRIDE_FLAG_DISABLE_PARAM = '$disableFeatureFlags';
const SESSION_STORAGE_USER = '$featureFlagUserId';

// The local variable toggles is used to store the enabled flag names
let toggles = new Set();

/**
 * retrieveToggles is a function to retrieve the feature toggles from a given URL.
 * (e.g., https://devapi.cloud.nuskin.com/feature-flags/v1/proxy)
 *
 * @param {string} url the URL for the retrieval of the feature flags
 * @param {string} userId an optional userId for the targeted user
 * @returns {Set<string>} a set of the currently-enabled feature flags
 */
async function retrieveToggles(url, userId) {
    // Make sure there is no query parameter for the URL
    if (url && (url.indexOf('?') === -1)) {
        // Make sure that the userId does NOT contain
        if (userId) {
            if (userId.indexOf('&') >= 0) {
                console.error('Unexpected character in userId:', userId);
            } else {
                url += `?userId=${userId}`;
            }
        } else {
            const ssUser = getSessionStorageUser()
            if(ssUser){
                url += `?userId=${ssUser}`;
            }
        }
        try {
            const response = await axios.get(url);
            if (Array.isArray(response.data.toggles)) {
                setTogglesFromResponse(response.data.toggles);
            } else {
                console.error('Invalid toggles (should be an array)');
            }
        } catch (err) {
            console.error('Cannot retrieve toggles:', err);
        }
    } else {
        console.error('Invalid url to retrieve feature flags, url=', url);
    }
    return getEnabledFlags();
}

/**
 * retrieveProductionToggles is a function to just retrieve the feature toggles from Production.
 *
 * @param {string} userId an optional userId for the targeted user
 * @returns {Set<string>} a set of the currently-enabled feature flags
 */
async function retrieveProductionToggles(userId) {
    return await retrieveToggles(FF_ENDPOINT, userId);
}

/**
 * isEnabled is a function to check if a given toggleName is enabled.
 *
 * @param {string} toggleName the name of the feature flag to check
 * @return {boolean} to indicate if the feature flag is enabled or not
 */
function isEnabled(toggleName) {
    return toggles.has(toggleName);
}

/**
 * Get the set of enabled flag names
 * @return {Set<string>} feature flag names
 */
function getEnabledFlags() {
    // Defensive copy
    return new Set([...toggles]);
}

//-------------------------------------------------------
//-------------- Private utility functions --------------
//-------------------------------------------------------

/**
 * From the server response array, set the toggles var, including any changes
 * from local user overrides.
 * @param {[]]} togglesArray array from server response
 */
function setTogglesFromResponse(togglesArray) {
    const localOverrides = getFlagOverrides();
    const fromServer = togglesArray.filter(t => t.enabled).map(t => t.name);

    // Turn into a nice, fast set
    const result = new Set(fromServer);

    // If we have an override...
    for (const [key, value] of localOverrides) {
        if (value) {
            // ...and it's true, make sure it's in the set
            result.add(key);
        } else {
            // ...otherwise, make sure it's NOT in the set
            result.delete(key);
        }
    }
    toggles = result;
}

/**
 * Get a map of all feature flag overrides from query string and session storage.
 * 
 * @return {Map<string, boolean>} flag names to override values
 */
function getFlagOverrides() {
    const disabledPairs = getOverrideList(OVERRIDE_FLAG_DISABLE_PARAM)
        .map(it => [it, false]);
    const enabledPairs = getOverrideList(OVERRIDE_FLAG_ENABLE_PARAM)
        .map(it => [it, true]);

    return new Map([...disabledPairs, ...enabledPairs]);
}

/**
 * Get a list of override names from query string and session storage
 * for a given parameter name.
 * 
 * @param {string} paramName param name
 * @return {string[]} feature flag names
 */
function getOverrideList(paramName) {
    const query = getQueryOverrides(paramName);
    const session = getSessionOverrides(paramName);

    return [...query, ...session];
}

/**
 * Get a list of feature names from the given query param
 * 
 * @param {string} paramName param name
 * @return {string[]} feature flag names
 */
function getQueryOverrides(paramName) {
    // If we're in a browser-ish environment, grab the querystring
    if ((typeof window !== "undefined") && window.location && window.location.search) {
        const query = new URLSearchParams(window.location.search);

        return query.getAll(paramName)
            .map(each => each.split(','))
            // flatMap for envs that don't yet support flatMap
            //  gotta do the flatMap because there can be multiple query params
            //  with the same name
            .reduce((a, each) => a.concat(...each), []);
    } else {
        return [];
    }
}

/**
 * Get a list of feature names from the given session storage value
 * 
 * @param {string} paramName param name
 * @return {string[]} feature flag names
 */
function getSessionOverrides(paramName) {
    /* istanbul ignore next */
    if ((typeof sessionStorage === "undefined") || !sessionStorage) {
        // If we're not in a browser, there's no session storage, so bail
        return [];
    }
    const found = sessionStorage.getItem(paramName);
    if (found) {
        return found.split(',');
    } else {
        return [];
    }
}

/**
 * getSessionStorageUser
 * 
 * @returns {Set<string>} a set of the currently-enabled feature flags
 */
function getSessionStorageUser() {
    let ffid = '';
    /* istanbul ignore next */
    if (typeof window !== "undefined"){
        /* istanbul ignore next */
        if (window && window.sessionStorage && window.sessionStorage.getItem(SESSION_STORAGE_USER)) {
            ffid = window.sessionStorage.getItem(SESSION_STORAGE_USER);
        }
    }

    return ffid;
}


module.exports = {
    retrieveToggles,
    retrieveProductionToggles,
    isEnabled,
    getEnabledFlags
};
